import { CurrencyCode } from '@dayetopia/library/types';
import { Offer, ProductPriceData, SEOPriceData } from '@contracts';
import { removeLocalesFromPath } from '@utils/language';
import useCurrentURLLanguage from './useCurrentUrlLanguage';

// European countries that should map to en-eu according to task requirements
const EUROPEAN_COUNTRIES = [
  'at',
  'be',
  'bg',
  'cy',
  'cz',
  'de',
  'dk',
  'ee',
  'es',
  'fi',
  'fr',
  'gr',
  'hr',
  'hu',
  'it',
  'lt',
  'lu',
  'lv',
  'mt',
  'nl',
  'pl',
  'ro',
  'se',
  'si',
  'sk'
];

// Available locales from i18n configuration
const AVAILABLE_LOCALES = {
  'en-gb': '', // Root domain (default)
  'en-us': '/en-us',
  'en-eu': '/en-eu',
  'en-ie': '/en-ie',
  'en-de': '/en-de',
  'en-bg': '/en-bg'
};

export const generateProductSchema = (productData: any, productPriceData: any[], seoTitle: string, description: string, uid?: string) => {
  const { productOptions } = productData;

  const localeToCurrencyMap = {
    'en-gb': 'GBP',
    'en-eu': 'EUR',
    'en-ie': 'EUR',
    'en-de': 'EUR',
    'en-bg': 'BGN',
    'en-us': 'USD'
  };

  const createOffer = (sku: string, priceData: SEOPriceData, price: ProductPriceData): Offer => ({
    '@type': 'Offer' as const,
    sku,
    name: priceData.name,
    price: price.amount.toFixed(2),
    priceCurrency: price.currency,
    itemCondition: 'http://schema.org/NewCondition',
    availability: 'http://schema.org/InStock'
  });

  const offers: Offer[] = [];

  const addOffers = (sku: string, priceData: SEOPriceData) => {
    if (!priceData) {
      return;
    }

    const currentLocale = useCurrentURLLanguage();

    if (!currentLocale || !currentLocale.locale) {
      return; // Return null if locale data is missing
    }

    const isValidLocale = (locale: string): locale is keyof typeof localeToCurrencyMap => {
      return locale in localeToCurrencyMap;
    };

    const targetCurrency = isValidLocale(currentLocale.locale) ? localeToCurrencyMap[currentLocale.locale] : undefined;

    const prices = priceData.pricePerPlan?.[0]?.price || priceData.price;
    const priceList = Array.isArray(prices)
      ? prices
          .filter((price) => price.currency === targetCurrency)
          .map((price) => {
            const finalPrice = { ...price };
            if (price.currency === CurrencyCode.GBP) {
              const vatPercent = priceData.vat?.GB ?? 0;
              finalPrice.amount = price.amount * (1 + vatPercent / 100);
            }
            return createOffer(sku, priceData, finalPrice);
          })
      : [];

    if (priceList) offers.push(...priceList);
  };

  productOptions.forEach((option: { one_off_sku?: { text: string }; subscription_sku?: { text: string } }) => {
    const { one_off_sku, subscription_sku } = option;

    if (one_off_sku?.text) {
      const priceData = productPriceData?.find((data) => data.id__normalized === one_off_sku.text);
      addOffers(one_off_sku.text, priceData);
    }

    if (subscription_sku?.text) {
      const priceData = productPriceData?.find((data) => data.id__normalized === subscription_sku.text);
      addOffers(subscription_sku.text, priceData);
    }
  });

  if (offers.length === 0) {
    const priceData = productPriceData.find((data) => data.id__normalized === productData.productCode);
    addOffers(productData.productCode, priceData);
  }

  if (!seoTitle || !description || offers.length === 0) {
    return null;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: seoTitle,
    image: productData.images[0]?.product_image.url || '',
    mpn: uid,
    brand: {
      '@type': 'Brand',
      name: 'Daye'
    },
    description,
    offers
  };

  return JSON.stringify(schema, null, 2);
};

/**
 * Processes Prismic manual hreflang data into the format expected by generateHreflangTags
 * Handles empty Prismic entries gracefully - filters out any entries without a valid locale
 * @param prismicHreflangs - The manual_hreflangs group field from Prismic
 * @returns Array of hreflang objects or null if no valid data
 */
export const processPrismicHreflangs = (prismicHreflangs?: any[]): Array<{ locale: string; url?: string }> | null => {
  if (!prismicHreflangs || !Array.isArray(prismicHreflangs) || prismicHreflangs.length === 0) {
    return null;
  }

  const validEntries = prismicHreflangs
    .filter((item) => {
      // Filter out empty Prismic entries - must have a locale with actual content
      return item && item.locale && typeof item.locale === 'string' && item.locale.trim().length > 0;
    })
    .map((item) => {
      const result: { locale: string; url?: string } = {
        locale: item.locale.trim()
      };

      // Only include URL if it exists and has content after trimming
      if (item.url && typeof item.url === 'string' && item.url.trim().length > 0) {
        result.url = item.url.trim();
      }

      return result;
    });

  // Return null if no valid entries after filtering
  return validEntries.length > 0 ? validEntries : null;
};

/**
 * Generates comprehensive hreflang tags according to task requirements
 * Uses i18n configuration for specific locales and European countries list for en-eu mapping
 * @param pathname - Current page pathname
 * @param baseUrl - Base URL from site metadata (e.g., 'https://www.yourdaye.com')
 * @param manualHreflangs - Optional manual hreflang overrides from Prismic
 * @returns Array of hreflang tag objects with hreflangTag and hrefTag properties
 */
export const generateHreflangTags = (pathname: string, baseUrl: string, manualHreflangs?: Array<{ locale: string; url?: string }>) => {
  const strippedPathname = removeLocalesFromPath(pathname);
  const hreflangTags: Array<{ hreflangTag: string; hrefTag: string }> = [];

  // If manual hreflangs are provided, use them instead of auto-generation
  if (manualHreflangs && manualHreflangs.length > 0) {
    // Check if both x-default and en-gb are present without explicit URLs
    const hasXDefault = manualHreflangs.some(({ locale, url }) => locale === 'x-default' && !url);
    const hasEnGb = manualHreflangs.some(({ locale, url }) => locale === 'en-gb' && !url);

    // If both x-default and en-gb are present without URLs, skip en-gb to avoid duplication
    const shouldSkipEnGb = hasXDefault && hasEnGb;

    manualHreflangs.forEach(({ locale, url }) => {
      // Skip en-gb if we already have x-default pointing to the same URL
      if (shouldSkipEnGb && locale === 'en-gb' && !url) {
        return;
      }

      let finalUrl: string;

      if (url) {
        finalUrl = url;
      } else if (locale === 'x-default' || locale === 'en-gb') {
        finalUrl = `${baseUrl}${strippedPathname}`;
      } else {
        finalUrl = `${baseUrl}${AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES] || `/${locale}`}${strippedPathname}`;
      }

      hreflangTags.push({
        hreflangTag: locale,
        hrefTag: finalUrl
      });
    });

    return hreflangTags;
  }

  // Generate automatic hreflang tags

  // 1. Add x-default pointing to root domain
  hreflangTags.push({
    hreflangTag: 'x-default',
    hrefTag: `${baseUrl}${strippedPathname}`
  });

  // 2. Add en-gb pointing to root domain
  hreflangTags.push({
    hreflangTag: 'en-gb',
    hrefTag: `${baseUrl}${strippedPathname}`
  });

  // 3. Add en-us pointing to /en-us/ path
  hreflangTags.push({
    hreflangTag: 'en-us',
    hrefTag: `${baseUrl}/en-us${strippedPathname}`
  });

  // 4. Add specific locales from i18n config (except ones already added)
  Object.entries(AVAILABLE_LOCALES).forEach(([locale, path]) => {
    if (locale !== 'en-gb' && locale !== 'en-us' && locale !== 'en-eu') {
      hreflangTags.push({
        hreflangTag: locale,
        hrefTag: `${baseUrl}${path}${strippedPathname}`
      });
    }
  });

  // 5. Add European countries pointing to /en-eu/ path (except those with specific locales)
  EUROPEAN_COUNTRIES.forEach((country) => {
    const countryTag = `en-${country}`;

    // Skip if this country already has a specific locale in i18n config
    if (countryTag in AVAILABLE_LOCALES) {
      return;
    }

    hreflangTags.push({
      hreflangTag: countryTag,
      hrefTag: `${baseUrl}/en-eu${strippedPathname}`
    });
  });

  return hreflangTags;
};
